import { Box } from "@mui/joy";
import { ApiLine } from "griptonite-api-client";
import minBy from "lodash/minBy";
import { useCallback, useEffect, useRef, useState } from "react";
import { useThrottledCallback } from "use-debounce";
import { useShallow } from "zustand/react/shallow";

import { useRoutesUiStore } from "@/components/RoutesProvider";
import { Route } from "@/core/types";

import { useVenueLinesQuery, useVenueRoutesByUuid } from "../../hooks/routes";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import { MapRoutePinDraggingSvg } from "./MapRoutePinSvg";
import { MOUSE_OFFSET, pointIsInElementBoundingClientRect } from "./MapsList";
import Transform from "./transform";

const cartesianDistance = (a: { x: number; y: number }, b: { x: number; y: number }) => {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
};

const getMapUuidAndPosition = (
  boxEl: HTMLDivElement | null | undefined,
  mousePos: { x: number; y: number },
) => {
  // We need to find the map that the user dragged this into,
  // get the screen coordinates relative to the map,
  // then invert the map's current zoom transform to get the final map position.
  if (!boxEl) {
    return {
      mapUuid: null,
      mapPosition: null,
    };
  }
  const unplacedRoutesEl = boxEl.parentElement?.getElementsByClassName("unplaced-routes")[0] as
    | HTMLDivElement
    | undefined;
  if (
    unplacedRoutesEl &&
    pointIsInElementBoundingClientRect(mousePos.x, mousePos.y, unplacedRoutesEl)
  ) {
    return {
      mapUuid: "UNPLACED-ROUTES",
      mapPosition: null,
    };
  }
  const mapElsIter = boxEl.parentElement?.getElementsByClassName("map-container") as
    | HTMLDivElement[]
    | undefined;

  const mapEls: HTMLDivElement[] = mapElsIter ? [...mapElsIter] : [];
  // Find the map el containing the final position in e
  const mapEl = mapEls.find((el) => pointIsInElementBoundingClientRect(mousePos.x, mousePos.y, el));
  if (!mapEl) {
    return {
      mapUuid: null,
      mapPosition: null,
    };
  }
  const rect = mapEl.getBoundingClientRect();
  const mapScreenCoords = [mousePos.x - rect.x, mousePos.y - rect.y];
  const mapTransform = mapEl.dataset.transform
    ? new Transform(...(JSON.parse(mapEl.dataset.transform) as [number, number, number]))
    : undefined;
  if (!mapTransform) {
    throw new Error("Map transform missing");
  }
  const mapPosition = {
    x: mapTransform.invertX(mapScreenCoords[0]),
    y: mapTransform.invertY(mapScreenCoords[1]),
  };
  const mapUuid = mapEl.dataset.uuid;
  if (!mapUuid) {
    throw new Error("Map uuid missing");
  }
  return { mapUuid, mapPosition };
};

const getClosestLine = (
  lines: ApiLine[] | undefined,
  mapUuid: string,
  mapPosition: { x: number; y: number },
) => {
  return minBy(
    lines?.filter(
      (line) =>
        line.map === mapUuid &&
        line.mapPosition &&
        Math.abs(line.mapPosition.x - mapPosition.x) < 50 &&
        Math.abs(line.mapPosition.y - mapPosition.y) < 50,
    ),
    (line) => cartesianDistance(line.mapPosition, mapPosition),
  );
};

export const CurrentlyDraggingRoutePin = () => {
  const setSelectedRouteUuids = useRoutesTableStore((state) => state.setSelectedRouteUuids);
  const {
    currentlyDraggingRouteUuid,
    setCurrentlyDraggingRouteUuid,
    setMapPositionEdit,
    setHighlightedLineUuid,
    highlightedLineUuid,
    routeFormEdits,
    createRouteData,
  } = useRoutesUiStore(
    useShallow((state) => ({
      currentlyDraggingRouteUuid: state.currentlyDraggingRouteUuid,
      setCurrentlyDraggingRouteUuid: state.setCurrentlyDraggingRouteUuid,
      setMapPositionEdit: state.setMapPositionEdit,
      setHighlightedLineUuid: state.setHighlightedLineUuid,
      highlightedLineUuid: state.highlightedLineUuid,
      routeFormEdits: state.routeFormEdits,
      createRouteData: state.createRouteData,
    })),
  );

  const currentVenueUuid = useCurrentVenueUuid();
  const routesMap = useVenueRoutesByUuid(currentVenueUuid);
  const { data: lines } = useVenueLinesQuery(currentVenueUuid);
  let route: Route | ({ uuid: string } & Partial<Route>) | null = null;
  if (currentlyDraggingRouteUuid) {
    if (createRouteData) {
      route = createRouteData;
    } else {
      route = routesMap?.[currentlyDraggingRouteUuid] || null;
    }
  }

  const [mousePos, setMousePos] = useState<{ x: number; y: number } | undefined>();

  const boxRef = useRef<HTMLDivElement | null>(null);

  const highlightNearestLineOnMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const clientX = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;

      const pinPos = {
        x: clientX - MOUSE_OFFSET.x,
        y: clientY - MOUSE_OFFSET.y,
      };

      const { mapPosition, mapUuid } = getMapUuidAndPosition(boxRef.current, pinPos);
      const closestLine =
        mapPosition && mapUuid ? getClosestLine(lines, mapUuid, mapPosition) : null;
      if ((closestLine?.uuid || null) !== highlightedLineUuid) {
        setTimeout(() => setHighlightedLineUuid(closestLine?.uuid || null), 0);
      }
    },
    [highlightedLineUuid, lines, setHighlightedLineUuid],
  );
  const throttledHighlightCallback = useThrottledCallback(highlightNearestLineOnMouseMove, 100);

  useEffect(() => {
    const isInCompetition =
      (route?.competitionRouteGroups?.length || 0) !== 0 ||
      (currentlyDraggingRouteUuid &&
        (routeFormEdits[currentlyDraggingRouteUuid]?.competitionRouteGroups?.length || 0) !== 0);
    const isSportLineMode = route?.climbType === "SPORT" && !isInCompetition;

    const mouseMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      const clientX = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;

      const parentBoundingClientRect = boxRef.current?.parentElement?.getBoundingClientRect();

      setMousePos({
        x: clientX - (parentBoundingClientRect?.x || 0),
        y: clientY - (parentBoundingClientRect?.y || 0),
      });
      if (isSportLineMode) {
        throttledHighlightCallback(e);
      }
    };
    const dragEnd = (e: MouseEvent | TouchEvent) => {
      const clientX = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.startsWith("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;

      if (!currentlyDraggingRouteUuid) {
        return;
      }

      const pinPos = {
        x: clientX - MOUSE_OFFSET.x,
        y: clientY - MOUSE_OFFSET.y,
      };

      const { mapUuid, mapPosition } = getMapUuidAndPosition(boxRef.current, pinPos);

      if (mapUuid === "UNPLACED-ROUTES") {
        setMapPositionEdit(currentlyDraggingRouteUuid, {
          line: null,
          map: null,
          x: null,
          y: null,
        });
        if (currentlyDraggingRouteUuid !== "CREATING") {
          setSelectedRouteUuids([currentlyDraggingRouteUuid]);
        }
        setCurrentlyDraggingRouteUuid(null);
        setMousePos(undefined);
        setHighlightedLineUuid(null);
        return;
      }

      if (mapPosition) {
        if (!mapUuid) {
          throw new Error("Map uuid missing");
        }
        if (isSportLineMode) {
          // find the nearest sport line on the map
          const closestLine = getClosestLine(lines, mapUuid, mapPosition);
          if (!closestLine) {
            setCurrentlyDraggingRouteUuid(null);
            setMousePos(undefined);
            setHighlightedLineUuid(null);
            return;
          }
          setMapPositionEdit(currentlyDraggingRouteUuid, {
            ...mapPosition,
            map: mapUuid,
            line: closestLine.uuid,
          });
          setCurrentlyDraggingRouteUuid(null);
          if (currentlyDraggingRouteUuid !== "CREATING") {
            setSelectedRouteUuids([currentlyDraggingRouteUuid]);
          }
          setMousePos(undefined);
          setHighlightedLineUuid(null);
          return;
        }
        setMapPositionEdit(currentlyDraggingRouteUuid, {
          ...mapPosition,
          map: mapUuid,
        });
        setCurrentlyDraggingRouteUuid(null);
        if (currentlyDraggingRouteUuid !== "CREATING") {
          setSelectedRouteUuids([currentlyDraggingRouteUuid]);
        }
        setMousePos(undefined);
        setHighlightedLineUuid(null);
      } else {
        setCurrentlyDraggingRouteUuid(null);
        setMousePos(undefined);
        setHighlightedLineUuid(null);
      }
    };

    if (currentlyDraggingRouteUuid) {
      window.addEventListener("touchmove", mouseMove, {
        capture: true,
        passive: false,
      });
      window.addEventListener("mousemove", mouseMove, { capture: true });
      window.addEventListener("mouseup", dragEnd, { capture: true });
      window.addEventListener("touchend", dragEnd, { capture: true });

      return () => {
        window.removeEventListener("touchmove", mouseMove, {
          capture: true,
        });
        window.removeEventListener("mousemove", mouseMove, { capture: true });
        window.removeEventListener("mouseup", dragEnd, { capture: true });
        window.removeEventListener("touchend", dragEnd, { capture: true });
      };
    }
  }, [
    currentlyDraggingRouteUuid,
    lines,
    route?.climbType,
    setCurrentlyDraggingRouteUuid,
    setHighlightedLineUuid,
    setMapPositionEdit,
    setSelectedRouteUuids,
    throttledHighlightCallback,
    route?.competitionRouteGroups?.length,
    routeFormEdits,
  ]);

  if (!route) {
    return;
  }

  const routeWithEdits = { ...route, ...routeFormEdits[route.uuid] };

  return (
    <Box
      ref={boxRef}
      sx={{
        position: "absolute",
        opacity: mousePos === undefined ? 0 : 1,
        ...(currentlyDraggingRouteUuid
          ? {
              top: (mousePos?.y || 0) - MOUSE_OFFSET.y,
              left: (mousePos?.x || 0) - MOUSE_OFFSET.x,
              zIndex: 1000,
              transform: `translate(-50%, -50%)`,
            }
          : undefined),
      }}
    >
      <MapRoutePinDraggingSvg route={routeWithEdits} />
    </Box>
  );
};
